import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Warehouse,
  ShoppingCart,
  Truck,
  Store,
  Receipt,
  BarChart3,
  Settings,
  ChevronLeft,
  Menu,
  Building2,
  Users,
  PackageCheck,
  X,
  Clock,
  Star,
  Archive,
  Activity,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { APP_VERSION } from '../../constants/version';
import { istBetaHost } from '../../utils/ebene';
import { InfoTooltip } from '../common/InfoTooltip';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { usePermission, type PermissionModule, type PermissionOperation } from '../../hooks/usePermission';

interface NavItem {
  key: string;
  path: string;
  icon: React.ReactNode;
  /**
   * Recht, ohne das der Punkt gar nicht erscheint.
   *
   * 🚨 Bis 28.08.2026 prüften **2 von 15** Punkten ein Recht — die übrigen 13 standen
   * jedem offen. Ben: „Hat ein User keine Rechte für einen Bereich, so soll er das erst
   * gar nicht sehen können, sondern nur den Navigationspunkt, auf den er Zugriff hat."
   *
   * Ohne Angabe bleibt der Punkt sichtbar — Dashboard und BIS Archiv hängen an anderen
   * Bedingungen (Lizenz), nicht an einem Modulrecht.
   */
  requiredPermission?: { module: PermissionModule; operation: PermissionOperation };
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
}

const navigationItems: NavItem[] = [
  { key: 'dashboard', path: '/', icon: <LayoutDashboard size={22} /> },
  { key: 'articles', path: '/articles', requiredPermission: { module: 'articles', operation: 'view' }, icon: <Package size={22} /> },
  { key: 'listings', path: '/listings', requiredPermission: { module: 'articles', operation: 'view' }, icon: <Store size={22} /> },
  { key: 'orders', path: '/orders', requiredPermission: { module: 'orders', operation: 'view' }, icon: <ShoppingCart size={22} /> },
  { key: 'crm', path: '/crm', requiredPermission: { module: 'customers', operation: 'view' }, icon: <Users size={22} /> },
  { key: 'accounting', path: '/accounting', requiredPermission: { module: 'accounting', operation: 'view' }, icon: <Receipt size={22} /> },
  { key: 'purchasing', path: '/purchasing', requiredPermission: { module: 'purchasing', operation: 'view' }, icon: <Building2 size={22} /> },
  { key: 'shipping', path: '/shipping', requiredPermission: { module: 'shipping', operation: 'view' }, icon: <Truck size={22} /> },
  { key: 'bisjustship', path: '/bisjustship', requiredPermission: { module: 'shipping', operation: 'view' }, icon: <PackageCheck size={22} /> },
  { key: 'inventory', path: '/inventory', requiredPermission: { module: 'inventory', operation: 'view' }, icon: <Warehouse size={22} /> },
  // Treueprogramm: seit V-1547 (c4dce441) gibt es das Modul `loyalty`. Werksrollen: Administrator und
  // Manager sehen + ändern, Buchhalter und Vertrieb sehen. Der Menüpunkt hängt an `loyalty.view`
  // (wie die Route per PermissionGate).
  //
  // 🚨 `timeTracking` bleibt bewusst OHNE Rechtebindung: Das Modul `time_tracking` gibt es, aber am
  // 29.08.2026 im Container gemessen führt es **keine einzige Werksrolle** — auch `admin`
  // nicht (der kommt nur über den Admin-Kurzschluss des Voters durch). Wer den Punkt daran
  // bindet, nimmt die Stempeluhr allen außer Administratoren weg. Erst gehört das Recht in
  // die Rollen, dann die Bindung hierher.
  { key: 'posLoyalty', path: '/pos-loyalty', requiredPermission: { module: 'loyalty', operation: 'view' }, icon: <Star size={22} /> },
  { key: 'timeTracking', path: '/time-tracking', icon: <Clock size={22} /> },
  { key: 'reports', path: '/reports', requiredPermission: { module: 'reports', operation: 'view' }, icon: <BarChart3 size={22} /> },
  { key: 'integrationStatus', path: '/integration-status', requiredPermission: { module: 'integrations', operation: 'view' }, icon: <Activity size={22} /> },
  { key: 'settings', path: '/settings', icon: <Settings size={22} /> },
];

export function Sidebar({ collapsed, onToggle, isMobile = false }: SidebarProps) {
  const { t } = useTranslation();
  const { hasFeature } = useFeatureGate();
  const { hasPermission } = usePermission();

  // 🚨 **Diese Leiste filterte bis 27.08.2026 überhaupt nichts** — jeder angemeldete Benutzer sah
  // jeden Menüpunkt, „Einstellungen" eingeschlossen. Gefiltert wird jetzt nur dieser eine Punkt,
  // und zwar am Recht `settings.edit` statt `settings.view`: Letzteres tragen **alle** Werksrollen
  // (auch Lager, Versand, Vertrieb, Buchhalter und Nur-Lesen), es hätte also niemanden
  // ausgeschlossen. `settings.edit` haben ab Werk Administrator und Manager — plus jeder, dem es
  // ausdrücklich zugewiesen wird. Administratoren kommen über `PermissionService::hasPermission()`
  // ohnehin immer durch.
  //
  // Die Route ist zusätzlich abgesichert (PermissionGate in App.tsx) und der Server verweigert das
  // Speichern eigenständig: Ein Menüpunkt, der nur verschwindet, ist kein Riegel.
  const darfEinstellungen = hasPermission('settings', 'edit');

  // BIS Archiv (revisionssicheres DMS) erscheint nur bei aktiver Lizenz dms_revisionssicher,
  // eingereiht direkt vor „Einstellungen". Die Route selbst ist zusätzlich FeatureGate-gegated.
  // 🚨 **Der Menuepunkt ist die Anzeige, nicht der Riegel.** Die Route ist zusaetzlich
  // abgesichert und der Server verweigert die Daten eigenstaendig (`Kostensicht`,
  // Modulrechte) — ein Punkt, der nur verschwindet, ist ueber die Adresszeile weiter
  // erreichbar.
  const items: NavItem[] = navigationItems.filter((i) => {
    if (i.key === 'settings') return darfEinstellungen;
    if (!i.requiredPermission) return true;
    return hasPermission(i.requiredPermission.module, i.requiredPermission.operation);
  });
  if (hasFeature('dms_revisionssicher')) {
    const archivItem: NavItem = { key: 'bisArchiv', path: '/bis-archiv', requiredPermission: { module: 'documents', operation: 'view' }, icon: <Archive size={22} /> };
    const settingsIdx = items.findIndex((i) => i.key === 'settings');
    if (settingsIdx >= 0) items.splice(settingsIdx, 0, archivItem);
    else items.push(archivItem);
  }

  // Determine the width class - mobile always shows full width sidebar
  const widthClass = isMobile ? 'w-64' : (collapsed ? 'w-16' : 'w-56');

  return (
    <aside
      className={`bg-slate-900 text-white h-screen sticky top-0 flex flex-col transition-all duration-300 ${widthClass}`}
    >
      {/* Header */}
      <div className={`p-4 border-b border-slate-700 flex items-center ${collapsed && !isMobile ? 'justify-center' : 'justify-between'}`}>
        {(!collapsed || isMobile) && (
          <div>
            {/* Wortmarke in der HELLEN Fassung — die Seitenleiste steht auf bg-slate-900.
                🚨 Der Pfad läuft über BASE_URL: Die App wird unter /app/ ausgeliefert
                   (Vite `base`, per VITE_BASE_PATH auch auf '/' umstellbar). Ein führender
                   Slash allein zeigte auf die Wurzel und damit ins Leere.
                🚨 Das Bild steckt im <h1>, damit die Überschriftenstruktur der Seite erhalten
                   bleibt; der Alternativtext trägt den Markennamen. */}
            <h1>
              <img
                src={`${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/bispicy-wortmarke-hell.svg`}
                alt="BISpicy"
                className="h-6 w-auto"
              />
            </h1>
            <p className="text-xs text-slate-400 mt-1">Warenwirtschaft</p>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-slate-800 transition-colors touch-manipulation"
          aria-label={isMobile ? 'Menü schließen' : (collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen')}
        >
          {isMobile ? <X size={22} /> : (collapsed ? <Menu size={22} /> : <ChevronLeft size={22} />)}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.key}
            to={item.path}
            end={item.path === '/'}
            title={(collapsed && !isMobile) ? t(`nav.${item.key}`) : undefined}
            onClick={isMobile ? onToggle : undefined}
            className={({ isActive }) =>
              `relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors touch-manipulation mb-1 ${
                (collapsed && !isMobile) ? 'justify-center' : ''
              } ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`
            }
          >
            {item.icon}
            {(!collapsed || isMobile) && <span className="text-sm">{t(`nav.${item.key}`)}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      {(!collapsed || isMobile) && (
        <div className="p-4 border-t border-slate-700 text-xs text-slate-500">
          Version {APP_VERSION}
          {istBetaHost() && (
            <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-500/20 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-amber-400">
              {t('ebene.betaBadge', 'Beta')}
              <InfoTooltip text={t('ebene.betaBadgeInfo', 'Sie arbeiten auf der Beta-Ebene von BIS ERP.')} size={11} position="top" />
            </span>
          )}
        </div>
      )}
    </aside>
  );
}
